import React, { useState, useEffect } from 'react';
import API from '../lib/api';
import { FileText, MessageSquare, Clock, Upload, ArrowUpRight, AlertTriangle, MessageCircle, CheckCircle, XCircle, Hourglass } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { useAuth } from '../contexts/AuthContext';

export default function WorkerDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [clockStatus, setClockStatus] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [communications, setCommunications] = useState([]);
  const [expiringDocs, setExpiringDocs] = useState([]);
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [loading, setLoading] = useState(true);
  const [elapsed, setElapsed] = useState('');

  useEffect(() => {
    fetchDashboardData();
  }, []);

  // Live elapsed timer
  useEffect(() => {
    if (!clockStatus?.is_clocked_in || !clockStatus?.current_record?.clock_in_time) return;
    
    const updateElapsed = () => {
      const start = new Date(clockStatus.current_record.clock_in_time);
      const diff = Math.floor((Date.now() - start.getTime()) / 1000);
      const h = Math.floor(diff / 3600);
      const m = Math.floor((diff % 3600) / 60);
      const s = diff % 60;
      setElapsed(`${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`);
    };
    updateElapsed();
    const interval = setInterval(updateElapsed, 1000);
    return () => clearInterval(interval);
  }, [clockStatus]);

  const fetchDashboardData = async () => {
    try {
      const [clockRes, docsRes, commsRes, expiringRes, notifRes] = await Promise.all([
        API.get('/clock-status').catch(() => ({ data: null })),
        API.get('/documents').catch(() => ({ data: [] })),
        API.get('/communications').catch(() => ({ data: [] })),
        API.get('/documents/expiring?days=30').catch(() => ({ data: [] })),
        API.get('/notifications/unread-count').catch(() => ({ data: { count: 0 } }))
      ]);
      setClockStatus(clockRes.data);
      setDocuments(Array.isArray(docsRes.data) ? docsRes.data : []);
      setCommunications(Array.isArray(commsRes.data) ? commsRes.data : []);
      setExpiringDocs(Array.isArray(expiringRes.data) ? expiringRes.data : []);
      setUnreadMessages(notifRes.data.count || 0);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-orange-200 border-t-orange-500 rounded-full animate-spin" />
      </div>
    );
  }

  const isClockedIn = clockStatus?.is_clocked_in;

  return (
    <div className="space-y-6" data-testid="worker-dashboard">
      {/* Header */}
      <div>
        <h1 className="text-2xl md:text-3xl font-semibold tracking-tight text-gray-900">
          Hola, {user?.name?.split(' ')[0]}
        </h1>
        <p className="text-sm text-gray-500 mt-0.5">
          {user?.company_name} — Tu portal de trabajo
        </p>
      </div>

      {/* Main Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Clock Status Hero */}
        <div className="lg:col-span-8 bg-white border border-gray-200 rounded-xl shadow-sm p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xs font-semibold tracking-widest text-gray-500 uppercase">Estado de fichaje</h2>
            <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${isClockedIn ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
              <div className={`w-2 h-2 rounded-full ${isClockedIn ? 'bg-green-500 animate-pulse' : 'bg-gray-400'}`} />
              {isClockedIn ? 'Trabajando' : 'Sin fichar'}
            </div>
          </div>

          {isClockedIn ? (
            <div className="text-center py-4">
              <p className="text-5xl md:text-6xl font-bold text-gray-900 font-mono tracking-wider" data-testid="clock-elapsed">
                {elapsed}
              </p>
              <p className="text-sm text-gray-500 mt-2">
                Entrada: {new Date(clockStatus.current_record.clock_in_time).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}
              </p>
            </div>
          ) : (
            <div className="text-center py-4">
              <p className="text-4xl font-bold text-gray-300">--:--:--</p>
              <p className="text-sm text-gray-500 mt-2">
                {clockStatus?.total_hours_today > 0 
                  ? `Has trabajado ${clockStatus.total_hours_today}h hoy`
                  : 'No has fichado hoy'}
              </p>
            </div>
          )}

          <div className="mt-4 flex justify-center">
            <Button
              onClick={() => navigate('/worker/time-clock')}
              className={`px-8 py-3 rounded-xl text-base font-semibold shadow-lg transition-all ${
                isClockedIn 
                  ? 'bg-red-500 hover:bg-red-600 text-white shadow-red-200' 
                  : 'bg-[#FF6B35] hover:bg-[#E55A2B] text-white shadow-orange-200'
              }`}
              data-testid="clock-action-btn"
            >
              <Clock className="w-5 h-5 mr-2" />
              {isClockedIn ? 'Fichar Salida' : 'Fichar Entrada'}
            </Button>
          </div>
        </div>

        {/* Right Column: Alerts & Quick Actions */}
        <div className="lg:col-span-4 space-y-4">
          {/* Document Expiration Alerts */}
          {expiringDocs.length > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle className="w-4 h-4 text-amber-600" />
                <p className="text-xs font-semibold text-amber-800 uppercase tracking-wider">Documentos por vencer</p>
              </div>
              <div className="space-y-2">
                {expiringDocs.slice(0, 3).map(doc => (
                  <div key={doc.id} className="flex items-center justify-between">
                    <span className="text-sm text-amber-900 truncate">{doc.document_type}</span>
                    <span className="text-xs text-amber-600 font-medium">{doc.expiry_date?.slice(0, 10)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Quick Actions */}
          <div className="space-y-2.5">
            <p className="text-xs font-semibold tracking-widest text-gray-500 uppercase px-1">Acciones</p>
            <QuickAction
              icon={Upload}
              label="Subir Documento"
              onClick={() => navigate('/worker/documents')}
            />
            <QuickAction
              icon={FileText}
              label="Partes de Trabajo"
              sublabel="Registra tu turno"
              onClick={() => navigate('/worker/work-reports')}
            />
            <QuickAction
              icon={FileText}
              label="Docs. para Firmar"
              onClick={() => navigate('/worker/signable-documents')}
            />
          </div>
        </div>
      </div>

      {/* Bottom Grid: Documents + Communications */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* My Documents */}
        <div className="lg:col-span-7 bg-white border border-gray-200 rounded-xl shadow-sm">
          <div className="p-5 border-b border-gray-100 flex items-center justify-between">
            <div>
              <h2 className="text-lg font-medium text-gray-900">Mis Documentos</h2>
              <p className="text-xs text-gray-500">{documents.length} documentos subidos</p>
            </div>
            <Button variant="ghost" size="sm" className="text-[#FF6B35] hover:bg-orange-50" onClick={() => navigate('/worker/documents')}>
              Ver todos
              <ArrowUpRight className="w-3.5 h-3.5 ml-1" />
            </Button>
          </div>
          <div className="divide-y divide-gray-100">
            {documents.length === 0 ? (
              <div className="p-8 text-center text-gray-400 text-sm">
                <FileText className="w-8 h-8 mx-auto mb-2 text-gray-200" />
                No tienes documentos subidos
              </div>
            ) : (
              documents.slice(0, 4).map(doc => (
                <div key={doc.id} className="px-5 py-3.5 flex items-center gap-3 hover:bg-gray-50/50 transition-colors">
                  <div className="w-8 h-8 bg-blue-50 rounded-lg flex items-center justify-center flex-shrink-0">
                    <FileText className="w-4 h-4 text-blue-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{doc.document_type}</p>
                    <p className="text-xs text-gray-500 truncate">{doc.original_filename}</p>
                  </div>
                  <DocStatusBadge status={doc.validation_status} />
                </div>
              ))
            )}
          </div>
        </div>

        {/* Recent Communications */}
        <div className="lg:col-span-5 bg-white border border-gray-200 rounded-xl shadow-sm">
          <div className="p-5 border-b border-gray-100 flex items-center justify-between">
            <div>
              <h2 className="text-lg font-medium text-gray-900">Comunicados</h2>
              <p className="text-xs text-gray-500">De tu empresa</p>
            </div>
            <Button variant="ghost" size="sm" className="text-[#FF6B35] hover:bg-orange-50" onClick={() => navigate('/worker/communications')}>
              Ver todos
              <ArrowUpRight className="w-3.5 h-3.5 ml-1" />
            </Button>
          </div>
          <div className="divide-y divide-gray-100">
            {communications.length === 0 ? (
              <div className="p-8 text-center text-gray-400 text-sm">
                <MessageSquare className="w-8 h-8 mx-auto mb-2 text-gray-200" />
                No hay comunicados
              </div>
            ) : (
              communications.slice(0, 4).map(comm => (
                <div
                  key={comm.id}
                  className="px-5 py-3.5 cursor-pointer hover:bg-gray-50/50 transition-colors"
                  onClick={() => navigate(`/worker/communications/${comm.id}`)}
                >
                  <p className="text-sm font-medium text-gray-900 truncate">{comm.subject}</p>
                  <p className="text-xs text-gray-500 mt-0.5 line-clamp-1">{comm.content}</p>
                  <p className="text-xs text-gray-400 mt-1">
                    {new Date(comm.created_at).toLocaleDateString('es-ES', { day: 'numeric', month: 'short' })}
                    {comm.reply_count > 0 && <span className="text-blue-500 ml-2">{comm.reply_count} respuestas</span>}
                  </p>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function QuickAction({ icon: Icon, label, sublabel, onClick, badge }) {
  return (
    <div
      onClick={onClick}
      className="bg-white border border-gray-200 rounded-xl p-3.5 flex items-center gap-3 cursor-pointer hover:shadow-md hover:-translate-y-0.5 transition-all duration-200"
      data-testid={`quick-action-${label.toLowerCase().replace(/\s/g, '-')}`}
    >
      <div className="w-9 h-9 bg-orange-50 rounded-lg flex items-center justify-center flex-shrink-0">
        <Icon className="w-4 h-4 text-[#FF6B35]" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-gray-900">{label}</p>
        {sublabel && <p className="text-xs text-gray-500">{sublabel}</p>}
      </div>
      {badge > 0 && (
        <span className="w-5 h-5 rounded-full bg-[#FF6B35] text-xs font-bold flex items-center justify-center text-white">
          {badge}
        </span>
      )}
      <ArrowUpRight className="w-3.5 h-3.5 text-gray-300 flex-shrink-0" />
    </div>
  );
}

function DocStatusBadge({ status }) {
  switch (status) {
    case 'approved':
      return (
        <Badge className="bg-green-50 text-green-700 text-xs gap-1">
          <CheckCircle className="w-3 h-3" />
          Aprobado
        </Badge>
      );
    case 'rejected':
      return (
        <Badge className="bg-red-50 text-red-700 text-xs gap-1">
          <XCircle className="w-3 h-3" />
          Rechazado
        </Badge>
      );
    case 'reviewed':
      return (
        <Badge className="bg-blue-50 text-blue-700 text-xs gap-1">
          Revisado
        </Badge>
      );
    default:
      return (
        <Badge className="bg-gray-100 text-gray-600 text-xs gap-1">
          <Hourglass className="w-3 h-3" />
          Pendiente
        </Badge>
      );
  }
}
